import { useRef, useState } from "react";
import Link from "next/link";
import SendCardModal from "@/components/ui/SendCardModal";
import WrapExperienceCard from "@/components/ui/WrapExperienceCard/WrapExperienceCard";
import { useAuthStore } from "@/services/Cognito/authStore";
import { trpc } from "@/services/Trpc/client";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import ThreeDots from "public/svgs/threeDots.svg";
import TrashIcon from "public/svgs/trash.svg";

import { type Card } from "@matan/database";

import DeleteCardModal from "./DeleteCardModal";

const CardListItem = ({ card }: { card: Card }) => {
  const [showEditName, setShowEditName] = useState(false);
  const [name, setName] = useState(card.name);
  const [showSend, setShowSend] = useState(false);
  const ref = useRef(null);

  const [modalShow, setModalShow] = useState(false);
  const lastUpdated = new Date(card.updatedAt);
  const user = useAuthStore((ctx) => ctx.user);

  const { mutate: mutateDuplicateCard } = trpc.card.create.useMutation();

  function duplicateCard() {
    mutateDuplicateCard({ cardId: card.id });
  }

  const { mutate: updateCard } = trpc.card.update.useMutation({
    onMutate: () => {
      setName("");
    },
    onSuccess: (updatedCard) => {
      setName(updatedCard.name);
    },
  });

  const handleSaveName = () => {
    if (showEditName) {
      setShowEditName(false);
      if (card.name !== name) updateCard({ id: card.id, name });
    }
  };

  return (
    <WrapExperienceCard
      modals={
        <SendCardModal
          isFree={card.isFree}
          showSend={showSend}
          setShowSend={setShowSend}
          cardId={card.id}
          cardName={card.name}
          cardThumbnailUrl={card.thumbnailURL}
        />
      }
      card={{
        ...card,
        author: user?.email || "",
        thumbnailURL: card.thumbnailURL || "",
      }}
      info={
        <>
          <WrapExperienceCard.TextTitle
            onClick={() => setShowEditName(true)}
            ref={ref}
            className="font-spaceGrotesk cursor-pointer text-ellipsis font-bold text-black"
          >
            {showEditName ? (
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSaveName();
                  }
                }}
              />
            ) : (
              name
            )}
          </WrapExperienceCard.TextTitle>
          <WrapExperienceCard.Text>
            {card.state === "SENT"
              ? `Sent To ${card.recipient}`
              : `Last Updated ${lastUpdated.toLocaleDateString()}`}
          </WrapExperienceCard.Text>
          <DeleteCardModal
            show={modalShow}
            setShow={setModalShow}
            id={card.id}
          />
        </>
      }
      image={<WrapExperienceCard.Image />}
      action={
        <Link
          href={`/preview?id=${card.id}&r=${Date.now().toString().slice(7)}`}
          rel="noopener noreferrer"
          className="h-full sm:grow"
        >
          <WrapExperienceCard.Button>Preview Card</WrapExperienceCard.Button>
        </Link>
      }
      topRight={
        <Popover>
          <PopoverButton>
            <div className="flex items-baseline rounded bg-white p-2">
              <div className="scale-120">
                <ThreeDots color={"black"} />
              </div>
            </div>
          </PopoverButton>
          <PopoverPanel anchor="bottom start">
            <PopoverButton>
              <div className="flex flex-col rounded bg-white">
                <Link
                  href={`../editor?id=${card.id}&r=${Date.now()
                    .toString()
                    .slice(7)}`}
                  className="h-full sm:grow"
                >
                  <div className="hover:bg-wg-gray flex items-center px-4 py-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-pen"
                      viewBox="0 0 16 16"
                    >
                      <path d="m13.498.795.149-.149a1.207 1.207 0 1 1 1.707 1.708l-.149.148a1.5 1.5 0 0 1-.059 2.059L4.854 14.854a.5.5 0 0 1-.233.131l-4 1a.5.5 0 0 1-.606-.606l1-4a.5.5 0 0 1 .131-.232l9.642-9.642a.5.5 0 0 0-.642.056L6.854 4.854a.5.5 0 1 1-.708-.708L9.44.854A1.5 1.5 0 0 1 11.5.796a1.5 1.5 0 0 1 1.998-.001zm-.644.766a.5.5 0 0 0-.707 0L1.95 11.756l-.764 3.057 3.057-.764L14.44 3.854a.5.5 0 0 0 0-.708l-1.585-1.585z" />
                    </svg>
                    <span className="ml-2 font-medium">Edit</span>
                  </div>
                </Link>

                <div
                  className="hover:bg-wg-gray flex items-center px-4 py-2"
                  onClick={duplicateCard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-front"
                    viewBox="0 0 16 16"
                  >
                    <path d="M0 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2h2a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2H2a2 2 0 0 1-2-2V2zm5 10v2a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-2v5a2 2 0 0 1-2 2H5z" />
                  </svg>
                  <span className="ml-2 font-medium">Duplicate</span>
                </div>

                <div
                  className="hover:bg-wg-gray flex items-center px-4 py-2"
                  onClick={() => setShowSend(true)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-share-fill"
                    viewBox="0 0 16 16"
                  >
                    <path d="M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.499 2.499 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5z" />
                  </svg>
                  <span className="ml-2 whitespace-nowrap font-medium">
                    {card.state === "DRAFT" ? "Send Card" : "Resend Card"}
                  </span>
                </div>

                <div
                  onClick={() => setModalShow(true)}
                  className="hover:bg-wg-gray flex items-center px-4 py-2 text-red-500 hover:cursor-pointer"
                >
                  <TrashIcon className="" />
                  <span className="ml-2 font-medium">Delete</span>
                </div>
              </div>
            </PopoverButton>
          </PopoverPanel>
        </Popover>
      }
    />
  );
};

export default CardListItem;
